import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import HotelSearchBar from "./HotelSearchBar";
import logo from '../Assets/Logo/logo.png';
import { auth } from '../config/firebase';

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const location = useLocation();

  const [imgError, setImgError] = useState(false);

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => {
      // Show navbar search after scrolling past hero search area (approx 400px)
      setScrolled(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track Auth State — support both token-based (phone OTP) and Firebase (Google)
  useEffect(() => {
    const token = localStorage.getItem('auth_token');

    const fetchBackendProfile = (token) => {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/+$/, '');
      fetch(`${apiBase}/auth/me`, {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          setUser({
            displayName: data.data.name,
            email: data.data.email,
            photoURL: data.data.avatar ? (data.data.avatar.startsWith('http') ? data.data.avatar : `${apiBase.replace('/api/v1', '')}/storage/${data.data.avatar}`) : null,
          });
        } else {
          setUser(null);
        }
      })
      .catch(() => setUser(null))
      .finally(() => setIsCheckingAuth(false));
      setImgError(false);
    };

    if (token) {
      fetchBackendProfile(token);
    } else {
      // Listen for Firebase auth as fallback (e.g., just logged in but token not set yet)
      const unsubscribe = auth.onAuthStateChanged((currentUser) => {
        if (currentUser) {
          setUser({
            displayName: currentUser.displayName,
            email: currentUser.email,
            photoURL: currentUser.photoURL,
          });
          setImgError(false);
        } else {
          setUser(null);
        }
        setIsCheckingAuth(false);
      });
      return () => unsubscribe();
    }
  }, [location]); // Re-check when route changes (e.g. after login)

  const getInitial = (user) => {
    if (user?.displayName) return user.displayName.charAt(0).toUpperCase();
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return 'U';
  };

  // Check if we're on the search page
  const isSearchPage = location.pathname === "/search";
  const isExplorePage = location.pathname === "/explore";
  const isAccount = location.pathname === "/account";
  
  // Pages where search bar should never show on scroll
  const hideSearchBarPages = ["/about-us", "/blogs", "/faq", "/offers", "/contact-us", "/hotel", "/booking", "/checkout"];
  const shouldHideSearchBar = hideSearchBarPages.some(page => 
    location.pathname === page || location.pathname.startsWith(`${page}/`)
  );

  const isHomePage = location.pathname === "/";
  const isConstrainedWidth = location.pathname.startsWith("/hotel/") || location.pathname.startsWith("/checkout") || location.pathname.startsWith("/booking") || location.pathname.startsWith("/faq");

  return (
    <nav
      className={`transition-all duration-300 z-50 ${
        isSearchPage || isExplorePage || isAccount
          ? "fixed top-0 left-0 right-0 hidden md:block bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" // always fixed + hide on mobile
          : scrolled
            ? "sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm"
            : "sticky top-0 bg-white border-b border-gray-100"
      }`}
    >
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 h-14 md:h-16 flex justify-between items-center ${isConstrainedWidth ? 'max-w-7xl' : 'w-full'}`}>

        {/* Logo */}
        <Link className="flex-shrink-0" to={'/'}>
          <img src={logo} className="h-8 md:h-10 w-auto object-contain" alt="Zoyo Trip" />
        </Link>

        {/* Nav Links or Search Bar */}
        {isSearchPage ? (
          <div className="flex-grow mx-4 lg:mx-8 max-w-4xl">
            <HotelSearchBar compact={true} />
          </div>
        ) : scrolled && window.innerWidth >= 1024 && !shouldHideSearchBar ? (
          <div className="flex-grow mx-4 lg:mx-8 max-w-4xl animate-fade-in-down">
            <HotelSearchBar compact={true} />
          </div>
        ) : (
          <div className="hidden lg:flex items-center gap-8 ml-auto mr-8">
            <a className="text-[13px] font-bold text-gray-600 hover:text-blue-600 transition-colors tracking-wider uppercase" href="/">Home</a>
            <a className="text-[13px] font-bold text-gray-600 hover:text-blue-600 transition-colors tracking-wider uppercase" href="/search">Hotels</a>
            <a className="text-[13px] font-bold text-gray-600 hover:text-blue-600 transition-colors tracking-wider uppercase" href="/about-us">About Us</a>
            <a className="text-[13px] font-bold text-gray-600 hover:text-blue-600 transition-colors tracking-wider uppercase" href="/blogs">Blogs</a>
            <a className="text-[13px] font-bold text-gray-600 hover:text-blue-600 transition-colors tracking-wider uppercase" href="/offers">Offers</a>
            <a className="text-[13px] font-bold text-gray-600 hover:text-blue-600 transition-colors tracking-wider uppercase" href="/faq">FAQs</a>
          </div>
        )}

        {/* Login/Signup or Profile Desktop */}
        <div className="hidden lg:block">
          {isCheckingAuth ? (
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gray-200 animate-pulse"></div>
          ) : user ? (
            <Link to="/account" className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full overflow-hidden border-2 border-white shadow-sm ring-2 ring-gray-100 hover:ring-blue-200 transition-all bg-gradient-to-br from-purple-500 to-indigo-600" title="My Profile">
              {user.photoURL && !imgError ? (
                <img src={user.photoURL} alt="Profile" className="w-full h-full object-cover" onError={() => setImgError(true)} />
              ) : (
                <span className="font-black text-white text-base">{getInitial(user)}</span>
              )}
            </Link>
          ) : (
            <Link className="bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white px-5 py-2 rounded-full font-bold transition-all shadow-sm text-sm" to="/login">
              Login / Signup
            </Link>
          )}
        </div>

        {/* Mobile Right: User Icon */}
        {!isSearchPage && (
          <div className="lg:hidden flex items-center">
            <Link to={user ? '/account' : '/login'} className="flex items-center justify-center">
              {isCheckingAuth ? (
                <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse"></div>
              ) : user ? (
                user.photoURL && !imgError ? (
                  <img src={user.photoURL} alt="Profile" className="w-8 h-8 rounded-full border border-gray-200 object-cover shadow-sm" onError={() => setImgError(true)} />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm border-2 border-white ring-1 ring-gray-200">
                    <span className="font-black text-white text-sm">{getInitial(user)}</span>
                  </div>
                )
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shadow-sm border border-blue-100">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/>
                    <path fillRule="evenodd" d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"/>
                  </svg>
                </div>
              )}
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
